import type { CheckoutEntitlementGrant } from "./checkout-contract";
import type { CheckoutEntitlementDecision } from "./checkout-events";
import {
  CURRENT_ENTITLEMENT_STATE,
  type EntitlementState,
  PAID_FEATURES,
  type PaidFeatureId,
} from "./paid-features";

export interface CheckoutEntitlementProjectionInput {
  grants: CheckoutEntitlementGrant[];
  revokedProviderPurchases?: CheckoutProviderPurchaseRef[];
  now?: string;
}

export interface CheckoutProviderPurchaseRef {
  provider: string;
  providerPurchaseId: string;
}

const PAID_FEATURE_IDS = new Set(Object.keys(PAID_FEATURES) as PaidFeatureId[]);

export function projectCheckoutEntitlementState({
  grants,
  revokedProviderPurchases = [],
  now = new Date().toISOString(),
}: CheckoutEntitlementProjectionInput): EntitlementState {
  const activeGrants = activeCheckoutEntitlementGrants({
    grants,
    revokedProviderPurchases,
    now,
  });

  if (activeGrants.length === 0) return CURRENT_ENTITLEMENT_STATE;

  const features = uniquePaidFeatures(activeGrants.flatMap((grant) => grant.features));
  if (features.length === 0) return CURRENT_ENTITLEMENT_STATE;

  return {
    plan: activeGrants.some((grant) => grant.plan === "lifetime") ? "lifetime" : "monthly",
    source: "verified_checkout",
    features,
  };
}

export function activeCheckoutEntitlementGrants({
  grants,
  revokedProviderPurchases = [],
  now = new Date().toISOString(),
}: CheckoutEntitlementProjectionInput): CheckoutEntitlementGrant[] {
  const revoked = new Set(revokedProviderPurchases.map(providerPurchaseKey));
  return grants.filter(
    (grant) =>
      grant.source === "verified_checkout" &&
      !revoked.has(providerPurchaseKey(grant)) &&
      !isExpired(grant, now),
  );
}

export function projectCheckoutDecisionState(
  decisions: CheckoutEntitlementDecision[],
  now = new Date().toISOString(),
): EntitlementState {
  const latestByPurchase = new Map<
    string,
    { occurredAt: string; grant?: CheckoutEntitlementGrant; revoked: boolean }
  >();

  for (const decision of decisions) {
    if (decision.type === "reject") continue;

    const purchaseKey =
      decision.type === "grant"
        ? providerPurchaseKey(decision.grant)
        : providerPurchaseKey(decision);
    const current = latestByPurchase.get(purchaseKey);
    if (current && Date.parse(current.occurredAt) > Date.parse(decision.occurredAt)) continue;

    latestByPurchase.set(
      purchaseKey,
      decision.type === "grant"
        ? { occurredAt: decision.occurredAt, grant: decision.grant, revoked: false }
        : { occurredAt: decision.occurredAt, revoked: true },
    );
  }

  return projectCheckoutEntitlementState({
    grants: [...latestByPurchase.values()]
      .filter((purchase) => purchase.grant && !purchase.revoked)
      .map((purchase) => purchase.grant as CheckoutEntitlementGrant),
    now,
  });
}

function isExpired(grant: CheckoutEntitlementGrant, now: string): boolean {
  if (grant.plan === "monthly" && !grant.expiresAt) return true;
  return !!grant.expiresAt && Date.parse(grant.expiresAt) <= Date.parse(now);
}

function uniquePaidFeatures(features: PaidFeatureId[]): PaidFeatureId[] {
  return [...new Set(features)].filter((feature) => PAID_FEATURE_IDS.has(feature));
}

function providerPurchaseKey({
  provider,
  providerPurchaseId,
}: CheckoutProviderPurchaseRef): string {
  return `${provider}:${providerPurchaseId}`;
}
